import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './SelectedProduct.css';
import TeamHeader from '../TeamHeader/TeamHeader';
import { useLocation } from "react-router-dom";
import { productConfigs } from './productConfig';
import orgsJson from '../../client-dashboard/client-dashboard-product-suite/ApiEndpoint/orgs_json.json';

// Assets
import fnnLogoBlack from '../../img/FNNLogoWithBlackFont-06.png';

// Icons
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

interface OrgUser {
    user_id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
}

interface OrgPermission {
    App: string;
    Category: 'Product' | 'Permission';
}

interface Org {
    id: number;
    ORG_NAME: string;
    ORG_LOGO: string | null;
    ORG_PLAN: string;
    ORG_PERMISSIONS: OrgPermission[];
    ORG_LOCATIONS: string[];
    API_ENDPOINT_ID: string | null;
    ORG_FOREFLIGHT_ID: string | null;
    PRODUCT_SUITE_COVERAGE_AREA: string | null;
    users: OrgUser[];
}

// Temp api endpoint interface
interface ApiEdpointInfo {
    apiID: number;
    Attributes: string[];
    AreaCoverage: string;
    Delivery: string;
}


const SelectedProduct = () => {
    const { productId } = useParams();
    const location = useLocation() as ReturnType<typeof useLocation> & {
        state?: { preselectedOrgId?: number };
    };
    const navigate = useNavigate();
    const config = productConfigs[productId || ""];

    const [orgs, setOrgs] = useState<Org[]>([]);
    const [detailsByOrg, setDetailsByOrg] = useState<Record<number, any>>({});
    const [loading, setLoading] = useState(true);

    const formatRegionName = (name: string) =>
        name.replace(/^detectors_/, '')
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    useEffect(() => {
        if (!config) return; // unknown product
        const fetchData = async () => {
            try {
                if (!config.api.orgList) return;

                const res = await fetch(config.api.orgList, { credentials: "include" });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const data = await res.json();
                const orgArray = Array.isArray(data) ? data : data.results || [];
                setOrgs(orgArray);

                // fetch org-specific details (or fallback to org.id)
                const results: Record<number, any> = {};
                for (const org of orgArray) {
                    if (config.api.details) {
                        const detailsUrl = config.api.details(org);
                        if (detailsUrl) {
                            const detailRes = await fetch(detailsUrl, { credentials: "include" });
                            if (detailRes.ok) {
                                results[org.id] = await detailRes.json();
                                continue;
                            }
                        }
                    }
                    // Fallback: just use org ID
                    results[org.id] = null;
                }

                setDetailsByOrg(results);
            } catch (err) {
                console.error(err);
                setOrgs([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [config]);


    if (!config) {
        return <div>Unknown product: {productId}</div>;
    }

    // Temp api endpoint interface
    const [apiInfo, setApiInfo] = useState<ApiEdpointInfo[]>([]);

    useEffect(() => {
        setApiInfo(orgsJson);
    }, []);

    const attributeDescriptions: { [key: string]: string } = {
        Date: "Timestamp of the detected strike",
        Coordinates: "Latitude and longitude of the strike",
        HRLRating: "A risk score from 0 to 100 indicating the potential danger of a lightning strike",
        Precipitation: "Total rainfall recorded at the strike location.",
        ERC: "Indicates potential fire intensity based on available fuels",
        Humidity: "Indicates atmospheric moisture content at the strike location",
        KBDI: "Indicates soil dryness and fire potential"
    };

    const rawDataAttributes: { [key: string]: string } = {
        Files: "Ability to view, download, and plot the files generated by the detectors.",
        Scope: "Ability to perform scope.",
    };

    const apiInfoMap: Record<number, ApiEdpointInfo> = {};
    orgsJson.forEach((item) => {
        apiInfoMap[item.apiID] = item;
    });

    // Showing popup
    const [activeSection, setActiveSection] = useState("");
    const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);
    const [selectedDetails, setSelectedDetails] = useState<any>(null);

    // Handling the Carousel logic
    const [startIndex, setStartIndex] = useState(0); // first banner shown
    const bannersPerPage = 4; // how many banners to show at once
    const [preselectedOrgId, setPreselectedOrgId] = useState<number | null>(
        location.state?.preselectedOrgId || null
    );

    useEffect(() => {
        if (!preselectedOrgId) return;
        if (!orgs.length || !Object.keys(detailsByOrg).length) return;

        const orgIndex = orgs.findIndex(o => o.id === preselectedOrgId);
        if (orgIndex === -1) return;

        const maxStart = Math.max(0, orgs.length - bannersPerPage);
        const clampedStart = Math.min(orgIndex, maxStart);
        setStartIndex(clampedStart);

        if (config?.popup) {
            const org = orgs[orgIndex];
            setSelectedOrg(org);
            setSelectedDetails(detailsByOrg[org.id]);
            setActiveSection(productId!);
        }

        // clear preselectedId so it won’t trigger again
        setPreselectedOrgId(null);
        navigate(location.pathname, { replace: true, state: {} });
    }, [preselectedOrgId, orgs, detailsByOrg, productId, config, navigate]);

    const hasRawDataAccess = (org: Org) => {
        return (
            org.ORG_PERMISSIONS?.some(
                (p) => p.Category === 'Product' && p.App === 'HRL Detector Data'
            ) &&
            org.ORG_LOCATIONS?.length > 0
        );
    };




    const handlePrev = () => {
        setStartIndex((prev) => Math.max(prev - 1, 0));
    };

    const handleNext = () => {
        setStartIndex((prev) =>
            Math.min(prev + 1, filteredOrgs.length - bannersPerPage)
        );
    };



    const filteredOrgs = orgs.filter((org) => {
        const details = detailsByOrg[org.id];

        switch (productId) {
            case "foreflight":
            case "email_dispatch":
                return !!details; // only orgs with details

            case "api_endpoint":
                return !!apiInfoMap[Number(org.API_ENDPOINT_ID)];

            case "hrl_detector_data":
                return hasRawDataAccess(org);

            default:
                return false;
        }
    });


    return (
        <div className="customer-directory-body">
            <TeamHeader title="Marketing" />
            <h1 className="cutomer-title">{config.title}</h1>


            <div className="selected-product-carousel-wrapper">
                <button className="selected-product-carousel-arrow" onClick={handlePrev} disabled={startIndex === 0}>
                    <IoIosArrowBack />
                </button>

                <div className="selected-product-banner-stack">
                    {filteredOrgs.slice(startIndex, startIndex + bannersPerPage).map((org) => {
                        const details = detailsByOrg[org.id];
                        return (
                            <div key={org.id} className="selected-product-banner">
                                <div className="selected-product-logo-and-name">
                                    <img src={org.ORG_LOGO || fnnLogoBlack} alt={org.ORG_NAME} />
                                    <h1>{org.ORG_NAME}</h1>
                                </div>

                                <div className="selected-product-info-wrapper">
                                    {/* -------- ForeFlight & Email Dispatch -------- */}
                                    {(productId === "foreflight" || productId === "email_dispatch") ? (
                                        details ? (
                                            <>
                                                <div className="selected-product-info">
                                                    <h2>Data Delivery</h2>
                                                    <p style={{ color: '#28aae1' }}>
                                                        {details.empty_email ? 'Daily Email' : 'Conditional Daily Email'}
                                                    </p>
                                                    <p>
                                                        {details.empty_email
                                                            ? 'Delivered even when there are no strikes.'
                                                            : 'Delivered only when strikes are detected.'}
                                                    </p>
                                                </div>

                                                <div className="selected-product-info">
                                                    <h2><strong>Area Coverage</strong></h2>
                                                    <p>{org.PRODUCT_SUITE_COVERAGE_AREA ?? 'N/A'}</p>
                                                </div>

                                                <div className="selected-product-info">
                                                    <h2><strong>HRL™ File Type</strong></h2>
                                                    <p>{details.hrl ? `HRL™ 0 - ${details.hrl}` : 'No HRL™ info available'}</p>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="no-setup-message">
                                                This organization does not have {config.title} set up.
                                            </p>
                                        )
                                    ) : null}

                                    {/* -------- API Endpoint -------- */}
                                    {productId === "api_endpoint" ? (
                                        apiInfoMap[Number(org.API_ENDPOINT_ID)] ? (
                                            <>
                                                <div className="selected-product-info">
                                                    <h2><strong>Data Delivery</strong></h2>
                                                    <p style={{ color: '#28aae1' }}>
                                                        {apiInfoMap[Number(org.API_ENDPOINT_ID)]?.Delivery}
                                                    </p>
                                                </div>

                                                <div className="selected-product-info">
                                                    <h2><strong>Area Coverage</strong></h2>
                                                    <p>{apiInfoMap[Number(org.API_ENDPOINT_ID)]?.AreaCoverage ?? 'N/A'}</p>
                                                </div>

                                                <div className="selected-product-info">
                                                    <h2><strong>Attributes</strong></h2>
                                                    <div className="attributes-table">
                                                        {apiInfoMap[Number(org.API_ENDPOINT_ID)]?.Attributes.map((attr, idx) => (
                                                            <div className="attribute-row" key={idx}>
                                                                <div className="attribute-name">{attr}</div>
                                                                <div className="attribute-description">{attributeDescriptions[attr]}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="no-setup-message">
                                                This organization does not have an {config.title} set up.
                                            </p>
                                        )
                                    ) : null}

                                    {/* -------- Raw Data -------- */}
                                    {productId === "hrl_detector_data" ? (
                                        hasRawDataAccess(org) ? (
                                            <>
                                                <div className="selected-product-info">
                                                    <h2><strong>Coverage Regions</strong></h2>
                                                    <ul>
                                                        {org.ORG_LOCATIONS.map((loc, idx) => (
                                                            <li key={idx}>{formatRegionName(loc)}</li>
                                                        ))}
                                                    </ul>
                                                </div>

                                                <div className="selected-product-info">
                                                    <h2><strong>Available Capabilities</strong></h2>
                                                    <div className="attributes-table">
                                                        {Object.entries(rawDataAttributes).map(([key, desc]) => (
                                                            <div className="attribute-row" key={key}>
                                                                <div className="attribute-name">{key}</div>
                                                                <div className="attribute-description">{desc}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="no-setup-message">
                                                This organization does not have access to {config.title}.
                                            </p>
                                        )
                                    ) : null}




                                    {/* {productId === "firesponse" && details && (
                                    <div className="selected-product-info">
                                        <h2>fiResponse Config</h2>
                                        <p>{details.some_field}</p>
                                    </div>
                                )} */}
                                </div>



                                {(productId === "foreflight" || productId === "email_dispatch") && details && (
                                    <div className="selected-product-footer">
                                        <button
                                            className="selected-product-info-wrapper-button"
                                            onClick={() => {
                                                setSelectedOrg(org);
                                                setSelectedDetails(details);
                                                setActiveSection("foreflight");
                                            }}
                                        >
                                            Select
                                        </button>
                                    </div>
                                )}

                            </div>
                        );
                    })}
                </div>

                <button
                    className="selected-product-carousel-arrow"
                    onClick={handleNext}
                    disabled={startIndex >= filteredOrgs.length - bannersPerPage}
                >
                    <IoIosArrowForward />
                </button>

            </div>

            {(activeSection === "foreflight" || activeSection === "email_dispatch") && selectedOrg && (
                <div className="active-products-popup-overlay">
                    <div className="selected-product-popup-content">

                        <button
                            className='close-product-popup-button'
                            onClick={() => {
                                setActiveSection("");
                                setSelectedOrg(null);
                                setSelectedDetails(null);
                            }}
                        >
                            Back
                        </button>

                        <div className="selected-product-info-wrapper inside-the-popup">

                            <div className='selected-product-popup-title-and-logo'>
                                <img src={selectedOrg.ORG_LOGO || fnnLogoBlack} alt={selectedOrg.ORG_NAME} />
                                <h1>{selectedOrg.ORG_NAME}</h1>

                            </div>

                            <div className="selected-product-info selected-product-popup-info">
                                <h2><strong>Data Delivery</strong></h2>
                                <p style={{ color: '#28aae1' }}>
                                    {selectedDetails?.empty_email ? 'Daily Email' : 'Conditional Daily Email'}
                                </p>
                                <p>
                                    {selectedDetails?.empty_email
                                        ? 'Delivered even when there are no strikes.'
                                        : 'Delivered only when strikes are detected.'}
                                </p>
                            </div>

                            <div className="selected-product-info selected-product-popup-info">
                                <h2><strong>Area Coverage</strong></h2>
                                <p>{selectedOrg.PRODUCT_SUITE_COVERAGE_AREA ?? 'N/A'}</p>
                            </div>

                            <div className="selected-product-info selected-product-popup-info">
                                <h2><strong>HRL™ File Type</strong></h2>
                                {selectedDetails?.hrl ? (
                                    <p>HRL™ 0 - {selectedDetails.hrl}</p>
                                ) : (
                                    <p>No HRL™ info available</p>
                                )}
                            </div>

                            {selectedDetails?.emails && selectedDetails.emails.length > 0 && (
                                <div className="selected-product-info selected-product-popup-info">
                                    <h2><strong>Recipients</strong></h2>
                                    <ul className='selected-product-popup-info-members'>
                                        {selectedDetails.emails.map((email: string, idx: number) => (
                                            <li key={idx}>{email}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default SelectedProduct;
